<template>
  <section class="space-y-4">
    <header class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          <i class="far fa-list-check text-primary-500 mr-2" />
          Applied Lists
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          <span v-tooltip="'Total unique (keyword, match type) pairs across all active applied lists.'"
            class="cursor-help underline decoration-dotted">
            {{ effectiveKeywordCount }} effective keywords
          </span>
          across {{ activeAppliedCount }} active list{{ activeAppliedCount === 1 ? '' : 's' }}.
        </p>
        <p class="text-xs text-gray-400 mt-1">
          "Applied" lists are what get exported to Google Ads. Inactive lists aren't deleted — they're just skipped during export.
        </p>
      </div>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        @click="openBrowser">
        <i class="far fa-plus mr-1" />
        Attach from library
      </button>
    </header>

    <div v-if="loading" class="flex items-center gap-2 text-sm text-gray-500">
      <UiSpinner :size="4" screen-reader="Loading applied lists" />
      <span>Loading applied lists…</span>
    </div>

    <div v-else-if="appliedLists.length === 0"
      class="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <i class="far fa-list-check text-3xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        No lists attached yet
      </p>
      <p class="text-sm text-gray-500 mt-1">
        Click "Attach from library" to pick lists for this Location. Attached lists feed the CSV export.
      </p>
    </div>

    <table v-else class="min-w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
      <thead class="bg-gray-50 dark:bg-gray-700">
        <tr>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Order
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            <span>Active</span>
            <i v-tooltip="'When inactive, this list is skipped during CSV export. The list itself is not deleted.'"
              class="far fa-circle-info text-primary-300 ml-1" />
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            List
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            <span>Scope</span>
            <i v-tooltip="SCOPE_HEADER_TOOLTIP"
              class="far fa-circle-info text-primary-300 ml-1" />
          </th>
          <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
            Keywords
          </th>
          <th class="px-3 py-2 w-10" />
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
        <tr v-for="(applied, idx) in appliedLists" :key="applied.id || applied.list_id">
          <td class="px-3 py-2">
            <button
              type="button"
              v-tooltip="'Move up — list with lower position wins for source attribution on duplicate keywords.'"
              class="text-gray-500 hover:text-gray-700 disabled:opacity-30"
              :disabled="idx === 0"
              @click="moveUp(idx)">
              ▲
            </button>
            <button
              type="button"
              v-tooltip="'Move down — list with higher position loses for source attribution on duplicate keywords.'"
              class="text-gray-500 hover:text-gray-700 disabled:opacity-30 ml-1"
              :disabled="idx === appliedLists.length - 1"
              @click="moveDown(idx)">
              ▼
            </button>
          </td>
          <td class="px-3 py-2">
            <input type="checkbox" v-model="applied.active" @change="commit({ silent: false })">
          </td>
          <td class="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
            {{ applied.list_name }}
          </td>
          <td class="px-3 py-2 text-sm text-gray-500">
            <span v-tooltip="SCOPE_TOOLTIPS[applied.list_scope] || ''"
              class="capitalize cursor-help">
              {{ applied.list_scope }}
            </span>
          </td>
          <td class="px-3 py-2 text-sm text-right text-gray-700">
            {{ applied.keyword_count }}
          </td>
          <td class="px-3 py-2 text-right">
            <button v-tooltip="'Detach from this Location. The list itself is preserved.'"
              class="text-red-500 hover:text-red-700" @click="detach(idx)">
              Detach
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'

const SCOPE_TOOLTIPS = {
  admin: 'Library — admin-curated, read-only.',
  account: 'Account — your account, available across all locations.',
  business: 'Business — scoped to this business.',
  location: 'This Location — scoped to this Location only.'
}

const SCOPE_HEADER_TOOLTIP = '<b>Library</b>: admin-curated, read-only.<br><b>Account</b>: your account.<br><b>Business</b>: this business.<br><b>This Location</b>: this Location only.'

const props = defineProps({
  locationId: { type: String, required: true },
  csrfToken: { type: String, required: true }
})

const emit = defineEmits(['attach-requested', 'effective-count-changed'])

const appliedLists = ref([])
const effectiveKeywordCount = ref(0)
const loading = ref(false)
const error = ref(null)

const activeAppliedCount = computed(() => appliedLists.value.filter((a) => a.active).length)

const fetchSet = async() => {
  loading.value = true
  error.value = null
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/negative_keyword_applied_set`, {
      headers: { 'Accept': 'application/json' }
    })
    if (!res.ok) throw new Error(`Load failed: ${res.status}`)
    const data = await res.json()
    appliedLists.value = data.applied_lists
    effectiveKeywordCount.value = data.effective_keyword_count
    emit('effective-count-changed', effectiveKeywordCount.value)
  } catch (e) {
    error.value = e.message
    toastError(e)
  } finally {
    loading.value = false
  }
}

const commit = async({ silent = true, successMessage = null } = {}) => {
  error.value = null
  const applied = appliedLists.value.map((a, idx) => ({
    list_id: a.list_id, active: a.active, position: idx
  }))
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/negative_keyword_applied_set`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' },
      body: JSON.stringify({ applied })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data.errors || ['Save failed']).join(', '))
    }
    const data = await res.json()
    appliedLists.value = data.applied_lists
    effectiveKeywordCount.value = data.effective_keyword_count
    emit('effective-count-changed', effectiveKeywordCount.value)
    if (!silent && successMessage) toastSuccess(successMessage)
  } catch (e) {
    error.value = e.message
    toastError(e)
  }
}

const moveUp = (idx) => {
  if (idx === 0) return
  const arr = [...appliedLists.value];
  [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]
  appliedLists.value = arr
  // Reorder is silent (don't spam on every click).
  commit()
}

const moveDown = (idx) => {
  if (idx === appliedLists.value.length - 1) return
  const arr = [...appliedLists.value];
  [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]
  appliedLists.value = arr
  commit()
}

const detach = (idx) => {
  const detached = appliedLists.value[idx]
  appliedLists.value.splice(idx, 1)
  commit({ silent: false, successMessage: `Detached "${detached?.list_name || 'list'}"` })
}

const openBrowser = () => emit('attach-requested', appliedLists.value.map((a) => a.list_id))

const attachLists = (newLists) => {
  // newLists: [{ id, name, scope, keyword_count }]
  newLists.forEach((list, i) => {
    appliedLists.value.push({
      list_id: list.id,
      list_name: list.name,
      list_scope: list.scope,
      keyword_count: list.keyword_count,
      active: true,
      position: appliedLists.value.length + i
    })
  })
  commit({
    silent: false,
    successMessage: `Attached ${newLists.length} list${newLists.length === 1 ? '' : 's'}`
  })
}

defineExpose({ attachLists, refresh: fetchSet })

onMounted(fetchSet)
</script>
